/*
 * <heritage-customer-signup>
 *
 * Posts a new self-serve signup (the {% form 'create_customer' %}
 * register form on the login page) to MDJ's EasySoft backend, which
 * creates the account.
 *
 * EasySoft does NOT just mirror the signup — it creates the Shopify
 * customer itself through the Admin API (records read "Braintree created
 * this customer" in admin and carry the legacy `normal-sign-up` tag,
 * applied server-side; that tag appears nowhere in this theme) and sends
 * both the customer welcome email and the MDJ team notification.
 *
 * So on a confirmed response (status 1) we do NOT submit the Shopify
 * form: the customer already exists by then, and create_customer would
 * fail "email has already been taken", dropping the partner on an error
 * page after a successful signup. Instead we sign the partner in with
 * the credentials they just chose (a native POST to /account/login with
 * a return_to) so they land signed-in on New Acquisitions with pricing
 * visible. The credit application is a secondary step from there, not
 * the first stop. A native form post (not fetch) on purpose: if Shopify
 * interposes its bot challenge, the browser follows it like any login,
 * and if the login somehow fails the partner lands on the sign-in page
 * with Shopify's own error — never a dead end.
 * Shopify stays the fallback for every other outcome — error status,
 * timeout, network failure, integration not configured — so an
 * unreachable EasySoft never leaves a partner with nothing.
 *
 * ── HISTORY, so nobody re-derives this the hard way ──────────────────
 * 2026-08-31: this push was removed at EasySoft's request and the form
 * reverted to Shopify-native. 2026-09-01: reinstated — Imran turned the
 * endpoint back on and added server-side anti-bot verification, so the
 * direct push is live again with hp_time + hp_client_name attached.
 *
 * CONTENT-TYPE IS LORE, NOT PREFERENCE:
 * /customer json_decodes the RAW body and REQUIRES application/json.
 * Sending application/x-www-form-urlencoded — the shape /credit and
 * /memo want, and the shape jQuery's legacy $.post used — comes back
 * "missing fields!" from this endpoint. text/plain with a JSON body also
 * works. Verified against the live endpoint 2026-06-24. Do not "fix"
 * this to match its sibling files.
 *
 * EasySoft contract:
 *   POST <endpoint>   body: JSON
 *     { email, first_name, last_name, cell_phone, store_name,
 *       store_phone, password, lead_id, hp_time, hp_client_name }
 *   Required (non-blank): email, first_name, last_name, store_name,
 *     store_phone, password. cell_phone + lead_id may be empty. No
 *     public_token (unlike the credit/memo endpoints).
 *   Response: { message, status, data }
 *     status 1 → "registered successfully!"
 *     status 0 → error; data.errArr e.g. ["Email: has already been taken"]
 *   #1 GOTCHA: a blank store_phone → "missing fields!", which is why the
 *   form collects it and marks it required.
 *
 * ANTI-BOT: hp_time (unix SECONDS at page load) and hp_client_name (the
 * honeypot, empty = human) come from snippets/form-spam-guard.liquid and
 * are forwarded for EasySoft to verify server-side. form-spam-guard.js
 * also checks them here, on a document-level capture listener that runs
 * ahead of this handler — so a submission it blocks never reaches the
 * fetch below. Client-side blocking stops bots that drive the page;
 * EasySoft's check stops anything POSTing the endpoint directly.
 *
 * SECURITY NOTE: the chosen password is forwarded to EasySoft — it's a
 * required field (EasySoft mirrors the login). EasySoft is MDJ's own
 * backend over HTTPS; the value is visible in the browser's network
 * panel like any client-side form post.
 *
 * Host attributes:
 *   data-easysoft-url    — endpoint URL (blank → integration disabled)
 *   data-submit-timeout  — ms before we give up and fall back (default 6000)
 *   data-post-signup-url — where the new partner lands once signed in
 *                          (default /collections/new-acquisitions)
 */
(function () {
  'use strict';

  /* Locale-aware account root — on a market with a language subpath,
   * Shopify.routes.root is '/en-ca/' rather than '/'. */
  function signinUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/+$/, '') + '/account/login';
  }

  /* Sign the just-created partner in via a native customer_login POST.
   * form.submit() fires no submit event, so form-spam-guard's capture
   * listener doesn't see (or block) this programmatic post. */
  function signIn(email, password, returnTo) {
    var form = document.createElement('form');
    form.method = 'post';
    form.action = signinUrl();
    form.acceptCharset = 'UTF-8';
    form.hidden = true;
    var fields = {
      form_type: 'customer_login',
      utf8: '\u2713',
      'customer[email]': email,
      'customer[password]': password,
      return_to: returnTo
    };
    Object.keys(fields).forEach(function (name) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = fields[name];
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  }

  class HeritageCustomerSignup extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.endpoint = this.dataset.easysoftUrl || '';
      this.submitTimeout = parseInt(this.dataset.submitTimeout || '6000', 10) || 6000;
      this.postSignupUrl = this.dataset.postSignupUrl || '/collections/new-acquisitions';
      this.form.addEventListener('submit', this.handleSubmit);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.handleSubmit);
    }

    fieldValue(name) {
      var el = this.form.querySelector('[name="' + name + '"]');
      return el ? (el.value == null ? '' : String(el.value)).trim() : '';
    }

    async handleSubmit(event) {
      if (this._submitting) return;
      // The native submit event fires only after the browser's built-in
      // required/format validation passes, so values are valid here.
      if (this.form.checkValidity && !this.form.checkValidity()) return;
      this._submitting = true;
      event.preventDefault();
      this.dataset.submitting = 'true';

      var result = null;
      try {
        result = await this.postToEasySoft(this.buildPayload());
      } catch (err) {
        if (window.console && console.warn) {
          console.warn('[heritage-customer-signup] EasySoft sync failed:', err);
        }
      }

      if (result && result.status === 1) {
        try {
          signIn(this.fieldValue('customer[email]'), this.fieldValue('customer[password]'), this.postSignupUrl);
        } catch (e) {
          // Couldn't build/post the login form — fall back to the manual
          // sign-in page with the "account is ready" notice.
          window.location.assign(signinUrl() + '?created=1');
        }
        return;
      }

      // EasySoft didn't confirm. Let Shopify create the account so the
      // partner isn't left with nothing, and fold the phone numbers into
      // the note first — Shopify's create_customer allow-list has no
      // phone field, so without this they'd be silently discarded on
      // exactly the path where EasySoft never received them either.
      this.foldPhonesIntoNote();
      this._submitting = false;
      delete this.dataset.submitting;
      this.form.submit();
    }

    /* Fallback-path only. Produces:
     *   Lee Jewelers
     *   Store phone: (555) 555-5555
     *   Cell phone: (555) 555-1234 */
    foldPhonesIntoNote() {
      var note = this.form.querySelector('[name="customer[note]"]');
      if (!note || this._folded) return;
      this._folded = true;

      var lines = [];
      var company = note.value.trim();
      if (company) lines.push(company);

      var storePhone = this.fieldValue('signup_store_phone');
      if (storePhone) lines.push('Store phone: ' + storePhone);

      var cellPhone = this.fieldValue('signup_cell_phone');
      if (cellPhone) lines.push('Cell phone: ' + cellPhone);

      note.value = lines.join('\n');
    }

    buildPayload() {
      return {
        email:       this.fieldValue('customer[email]'),
        first_name:  this.fieldValue('customer[first_name]'),
        last_name:   this.fieldValue('customer[last_name]'),
        cell_phone:  this.fieldValue('signup_cell_phone'),
        // Company name is collected via customer[note] (Shopify's
        // allow-listed field); EasySoft calls it store_name.
        store_name:  this.fieldValue('customer[note]'),
        store_phone: this.fieldValue('signup_store_phone'),
        password:    this.fieldValue('customer[password]'),
        lead_id:     '',
        // EasySoft's anti-bot fields. An EMPTY hp_client_name is the
        // pass case, so it must go over as '' rather than be omitted.
        hp_time:        this.fieldValue('hp_time'),
        hp_client_name: this.fieldValue('hp_client_name')
      };
    }

    /* Returns the parsed response, or null when we didn't get a usable
     * one. handleSubmit treats null/non-1 as "EasySoft did not create the
     * account" and falls back to Shopify, so every early return here must
     * be an explicit null. */
    async postToEasySoft(payload) {
      if (!this.endpoint) return null;                  // not configured → skip
      // EasySoft rejects with "missing fields!" when a required value is
      // absent, so skip rather than fire a guaranteed-failing request.
      if (!payload.email || !payload.store_phone) return null;

      var controller = new AbortController();
      var timeoutId = window.setTimeout(function () { controller.abort(); }, this.submitTimeout);
      try {
        var res = await fetch(this.endpoint, {
          method: 'POST',
          // See CONTENT-TYPE IS LORE above — this endpoint requires JSON.
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload),
          mode: 'cors',
          credentials: 'omit',
          signal: controller.signal,
          keepalive: true
        });
        if (!res || !res.ok) return null;
        var body = null;
        try {
          body = await res.json();
        } catch (e) {
          return null;                                  // non-JSON / opaque
        }
        if (body && body.status !== 1 && window.console && console.warn) {
          console.warn('[heritage-customer-signup] EasySoft did not register the customer:', body.message || body);
        }
        return body;
      } finally {
        window.clearTimeout(timeoutId);
      }
    }
  }

  if (!customElements.get('heritage-customer-signup')) {
    customElements.define('heritage-customer-signup', HeritageCustomerSignup);
  }

  /* Reveal the "account is ready" notice after the ?created=1 fallback
   * redirect above (only reached when the auto sign-in can't run). Liquid
   * can't read query params, so the markup ships hidden. Strips the param
   * afterwards so a refresh doesn't keep asserting a fresh signup. */
  function revealCreatedNotice() {
    if (window.location.search.indexOf('created=1') === -1) return;
    var notice = document.getElementById('signin-created-notice');
    if (notice) notice.hidden = false;
    if (window.history && window.history.replaceState) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', revealCreatedNotice);
  } else {
    revealCreatedNotice();
  }
})();
